import { Box, Stack, SvgIcon, type SvgIconProps } from "@mui/material";
import type { GetAvailabilitiesResponse } from "../../shared/web/schema/getAvailabilities";
import ClassNames from "../../util/ClassNames";
import { ColorHexCodes } from "../../util/Color";
import Time from "../../util/Time";
import { StyledText } from "../StyledText";
import style from "./TimeTable.module.css";

type TimeTableArgs = {
  page: number;
  availability: GetAvailabilitiesResponse;
  onPaging: (page: number) => void;
  onSelected: (unixTime: number) => void;
};

export default function TimeTable({ page, availability, onPaging, onSelected }: TimeTableArgs) {
  // スロットオフセット（時間）を取得
  const slots = availability.slot_offsets_millis;

  // 列のラベル(日付)を作成する - daily_availabilitiesのstart_of_day_millisを使用
  const daysLabelItem = createDaysLabel(availability.daily_availabilities);

  // 行のラベル(時間)を作成する
  const timeLabels = createTimeLabels(slots);

  // 予約セルを作成する
  const dataCells = createDataCells(slots, availability, onSelected);

  return (
    <Stack className={ClassNames(style.cellWrapper)} sx={{ textAlign: "center" }}>
      <Stack className={ClassNames(style.cell)} direction={"row"}>
        <Stack
          className={page > 0 ? ClassNames(style.clickable) : ""}
          direction={"row"}
          onClick={() => {
            page > 0 && onPaging(page - 1);
          }}
          sx={{ color: page > 0 ? "black" : "gray", p: "10px" }}
        >
          <TriangleArrowIcon arrowDirection={"left"} />
          前の週
        </Stack>
        <Stack className={ClassNames(style.grow)} />
        <Stack className={ClassNames(style.clickable)} direction={"row"} onClick={() => onPaging(page + 1)} sx={{ p: "10px" }}>
          次の週
          <TriangleArrowIcon />
        </Stack>
      </Stack>
      <Stack bgcolor="white" direction={"row"} position="sticky" sx={{ top: 0 }}>
        <Box className={ClassNames(style.grow, style.cell, style.timeColumn)}>日時</Box>
        {daysLabelItem}
      </Stack>
      <Stack direction={"row"}>
        <Stack className={ClassNames(style.grow, style.timeColumn)}>{timeLabels}</Stack>
        {dataCells}
      </Stack>
    </Stack>
  );
}

function createDaysLabel(dailyAvailabilities: GetAvailabilitiesResponse["daily_availabilities"]) {
  return dailyAvailabilities.map((dayData) => {
    const timeString = new Time(dayData.start_of_day_millis).DateStrings();
    let dateColor = ColorHexCodes.CharacterBlack;
    let dayColor = "7a7a7a";
    if (timeString.day === "土") {
      dateColor = "688feb";
      dayColor = "688feb";
    } else if (timeString.day === "日") {
      dateColor = ColorHexCodes.RequiredRed;
      dayColor = ColorHexCodes.RequiredRed;
    }
    return (
      <Stack className={ClassNames(style.grow, style.cell)} key={dayData.start_of_day_millis}>
        <Stack style={{ color: `#${dateColor}` }}>
          <StyledText textStyle="NoMargin">{`${timeString.month}/${timeString.date}`}</StyledText>
        </Stack>
        <Stack style={{ color: `#${dayColor}` }}>
          <StyledText textStyle="TimeTableDay">{`${timeString.day}`}</StyledText>
        </Stack>
      </Stack>
    );
  });
}

function createTimeLabels(slots: number[]) {
  return slots.map((slot) => {
    const totalMinutes = slot / Time.AMinute;
    const hour = Math.floor(totalMinutes / 60);
    const min = (totalMinutes % 60).toString().padStart(2, "0");
    return (
      <Box className={ClassNames(style.cell, style.timeColumn)} key={slot} style={{ height: "45px" }}>
        {`${hour}:${min}`}
      </Box>
    );
  });
}

function createDataCells(slots: number[], availability: GetAvailabilitiesResponse, onSelected: (unixTime: number) => void) {
  // 日付ごとのデータがdaily_availabilitiesとして返ってくる
  return availability.daily_availabilities.map((dayData) => {
    const startOfDay = dayData.start_of_day_millis;
    const column = slots.map((slotOffset, slotIndex) => {
      const isAvailable = dayData.availability_flags[slotIndex] ?? false;
      const timeStamp = startOfDay + slotOffset;
      if (isAvailable) {
        return (
          <Box className={ClassNames(style.cell, style.clickable, style.availablityCell)} key={slotOffset} onClick={() => onSelected(timeStamp)} style={{ color: `#${ColorHexCodes.RequiredRed}` }}>
            ◎
          </Box>
        );
      } else {
        return (
          <Box className={ClassNames(style.cell, style.unavailable, style.availablityCell)} key={slotOffset}>
            ×
          </Box>
        );
      }
    });
    return (
      <Stack className={style.grow} key={dayData.start_of_day_millis}>
        {column}
      </Stack>
    );
  });
}

interface TriangleArrowIconProps extends SvgIconProps {
  arrowDirection?: "left" | "right";
}
const TriangleArrowIcon: React.FC<TriangleArrowIconProps> = ({ arrowDirection = "right", ...props }) => {
  const points = arrowDirection === "left" ? "15,5 9,10 15,15" : "5,5 11,10 5,15";
  return (
    <SvgIcon {...props}>
      <polygon points={points} />
    </SvgIcon>
  );
};
